import React from "react";

export interface ClientAuth {
    accessToken: string;
    clientId: number;
    phone: string;
    expiresAt: number;
    isVerified: boolean;
    name?: string;
}

interface ClientAuthState {
    auth: ClientAuth | null;
    isLoggedIn: boolean;
    isVerified: boolean;
}

interface ClientAuthActions {
    login(auth: ClientAuth): void;
    logout(): void;
    setVerified(verified: boolean): void;
}

const Ctx = React.createContext<{ state: ClientAuthState; actions: ClientAuthActions } | null>(null);

const STORAGE_KEY = "client_auth";

function loadFromStorage(): ClientAuth | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ClientAuth;
        if (parsed.expiresAt < Date.now()) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

/** Check if client is logged in (can be used outside ClientAuthProvider) */
export function isClientLoggedIn(): boolean {
    const auth = loadFromStorage();
    return !!auth && auth.expiresAt > Date.now();
}

/** Get stored client auth data (can be used outside ClientAuthProvider) */
export function getStoredClientAuth(): ClientAuth | null {
    return loadFromStorage();
}

export function ClientAuthProvider({ children }: { children: React.ReactNode }) {
    const [auth, setAuth] = React.useState<ClientAuth | null>(() => loadFromStorage());

    const actions: ClientAuthActions = React.useMemo(() => ({
        login(newAuth: ClientAuth) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newAuth));
            setAuth(newAuth);
        },
        logout() {
            localStorage.removeItem(STORAGE_KEY);
            setAuth(null);
        },
        setVerified(verified: boolean) {
            if (auth) {
                const updated = { ...auth, isVerified: verified };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                setAuth(updated);
            }
        },
    }), [auth]);

    const state: ClientAuthState = {
        auth,
        isLoggedIn: !!auth && auth.expiresAt > Date.now(),
        isVerified: auth?.isVerified ?? false,
    };

    return <Ctx.Provider value={{ state, actions }}>{children}</Ctx.Provider>;
}

export function useClientAuth() {
    const ctx = React.useContext(Ctx);
    if (!ctx) throw new Error("useClientAuth must be used within ClientAuthProvider");
    return ctx;
}
